import { User as AuthentikUser, UserManager, type UserManagerSettings } from 'oidc-client-ts';

import { ref, computed, ComputedRef, reactive, UnwrapNestedRefs } from 'vue';
import * as UserService from '@/services/api/userService';
import { User } from '@/types/api/User/User';
import { Role } from '@/types/enum/user';
import { Domain } from '@/types/api/Business/Domain';
import { Supplier } from '@/types/api/User/Supplier';
import { Reseller } from '@/types/api/User/Reseller';
import { RuntimeError } from '@/types/api/Utility/RuntimeError';
import { RouteLocation } from 'vue-router';

interface AuthStore {
  user: ComputedRef<User | null>;
  roles: ComputedRef<Role[] | null>;

  isAuthenticated: ComputedRef<boolean>;
}

interface DomainStore {
  domains: ComputedRef<Domain[]>;

  primaryDomain: ComputedRef<Domain | null>;
  supplier: ComputedRef<string | null>;
}

interface CombinedStore extends AuthStore, DomainStore {}

type ReactiveAuthStore = UnwrapNestedRefs<AuthStore>;
type ReactiveDomainStore = UnwrapNestedRefs<DomainStore>;

interface ReactiveCombinedStore extends ReactiveAuthStore, ReactiveDomainStore {}

class DummyReactiveCombinedStore implements ReactiveCombinedStore {
  //dummy fields which get replaced in constructor
  public user: User | null = null;
  public roles: Role[] | null = null;
  public isAuthenticated = false;
  public domains: Domain[] = [];
  public primaryDomain: Domain | null = null;
  public supplier: string | null = null;

  protected privateFields = reactive({
    user: ref<User | null>(null),
    roles: ref<Role[] | null>(null),
    domains: ref<Domain[]>([]),
  });

  constructor() {
    const store: AuthStore = {
      user: computed(() => this.privateFields.user as User),
      roles: computed(() => this.privateFields.roles as Role[]),
      isAuthenticated: computed(() => store.user.value != null),
    };

    const domainStore: DomainStore = {
      domains: computed(() => this.privateFields.domains as Domain[]),
      primaryDomain: computed(() => {
        if (domainStore.domains.value.length > 0) {
          return domainStore.domains.value[0] as Domain;
        }
        return null;
      }),
      supplier: computed(() => {
        if (domainStore.domains.value.length > 0) {
          return domainStore.domains.value[0].urlSuffix ?? null;
        }
        return null;
      }),
    };

    const reactiveStore: ReactiveCombinedStore = reactive<CombinedStore>(Object.assign(this, store, domainStore));
    return reactiveStore as DummyReactiveCombinedStore;
  }
}

export default class Auth extends DummyReactiveCombinedStore {
  private userManager: UserManager | null = null;
  private static instance: Auth;
  private _runtimeErrorOccured = false;

  /**
   * Settings for the UserManager object.
   */
  settings: UserManagerSettings = {
    authority: import.meta.env.VITE_AUTHROITY_URL ?? '',
    client_id: import.meta.env.VITE_CLIENT_KEY ?? '',
    redirect_uri: import.meta.env.VITE_REDIRECT_URL ?? '',
    response_type: import.meta.env.VITE_REPONSE_TYPE,
    scope: import.meta.env.VITE_SCOPE,
    client_secret: import.meta.env.VITE_SECRET,
    post_logout_redirect_uri: import.meta.env.VITE_REDIRECT_URL,
    revokeTokensOnSignout: true,
  };

  protected setUser(value: User | null) {
    this.privateFields.user = value;

    if (this.roles?.includes(Role.Supplier)) {
      this.setSupplier(this.user as Supplier);
    } else if (this.roles?.includes(Role.Reseller)) {
      this.setSupplier((this.user as Reseller)?.supplier);
    }
  }

  protected setSupplier(supplier: Supplier | null): void {
    this.privateFields.domains = supplier?.domains ?? [];
    localStorage.setItem('supplier', this.primaryDomain?.urlSuffix ?? '');
  }

  public hasRuntimeErrorOccured(): boolean {
    return this._runtimeErrorOccured;
  }

  constructor() {
    super();

    this.fetchUserManager();
  }

  public static getInstance(): Auth {
    if (Auth.instance === undefined) {
      Auth.instance = new Auth();
    }
    return Auth.instance;
  }

  /**
   * Tries to find an UserManager instance in the local storage. If not found, creates a
   * new instance.
   */
  private fetchUserManager(): void {
    // Search for instance in local storage.
    const storageKeys = Object.keys(sessionStorage);
    const userManagerKey = storageKeys.find((key) => key.startsWith('oidc.'));
    const userManagerJson = userManagerKey ? localStorage.getItem(userManagerKey) : null;
    const userManagerObject = userManagerJson ? new UserManager(JSON.parse(userManagerJson)) : null;
    // No instance found, create a new one.
    if (userManagerObject == null) {
      const queryParams = new URLSearchParams(window.location.search);
      const supplier = queryParams.get('supplier');
      const newSetting: UserManagerSettings = {
        ...this.settings,
        extraQueryParams: {
          supplier: supplier ?? '',
        },
      };
      this.userManager = new UserManager(newSetting);
      return;
    }
    this.userManager = userManagerObject;
  }

  /**
   * Redirects to log in. Should be called from a button.
   * @param event Event (Button Press)
   */
  public useAuth(event: Event): void {
    event.preventDefault();
    // Invalid user manager, abort.
    if (this.userManager == null) {
      this.fetchUserManager();
      return;
    }

    this.userManager.signinRedirect();
  }

  private getStateFromQueryParams(): string | null {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('state');
  }

  private clearState(): void {
    localStorage.setItem('token', '');
    localStorage.setItem('access_token', '');
    localStorage.setItem('login_state', '');

    this.userManager?.removeUser();
    this.userManager?.clearStaleState();
  }

  public async handleSupplierFromRoute(route: RouteLocation): Promise<boolean> {
    let supplier: string | null = null;

    if (route.query.supplier) supplier = route.query.supplier.toString();
    else if (route.params.supplier) supplier = route.params.supplier.toString();

    if (supplier != null) {
      const domains = [await UserService.getDomainByUrlSuffix(supplier)].filter((d) => d?.urlSuffix != null);

      if (domains && domains.length > 0 && domains[0]?.urlSuffix) {
        this.privateFields.domains = domains;
        supplier = this.primaryDomain?.urlSuffix ?? '';

        const supplierFromLocalStorage = localStorage.getItem('supplier') ?? null;
        if (supplierFromLocalStorage != null && supplier != supplierFromLocalStorage) {
          console.warn(`Supplier has changed... (${supplierFromLocalStorage} -> ${supplier})`);
        }
        localStorage.setItem('supplier', supplier);
      }
    }
    return supplier != null;
  }

  /**
   * The basic authentication flow. First check if user cookie exists and validates.
   * @returns Promise<void>
   */
  public async handleAuthentication(): Promise<void> {
    // Invalid user manager, abort
    if (this.userManager == null) {
      this.fetchUserManager();
      return;
    }

    const state = this.getStateFromQueryParams();
    const stateFromLocalStorage = localStorage.getItem('login_state');

    //if there is a different state param, the user has been redirected from the authorization flow,
    //but no proper logout has happened before
    const isRedirected = state != null && state != stateFromLocalStorage;
    if (isRedirected) {
      this.clearState();
    }

    // Try to get saved user object.
    const authentikUser = await this.userManager.getUser();
    // Check token expiring date.
    if (authentikUser && authentikUser.expires_at && authentikUser.expires_at < Date.now() / 1000) {
      await this.authorizeUser(authentikUser);
      return;
    }
    if (!isRedirected) {
      //No "state" param found in response url
      if (authentikUser && !this.isAuthenticated) {
        await this.authorizeUser(authentikUser);
      }
      return;
    }
    localStorage.setItem('login_state', state);

    // Redirect user.
    await this.userManager
      .signinRedirectCallback()
      .then(async (authentikUser) => {
        // User authenticated.
        await this.authorizeUser(authentikUser);
      })
      .catch((err) => {
        if (err instanceof RuntimeError) {
          this._runtimeErrorOccured = true;
          throw err;
        }

        // User unauthenticated.
        console.error(err);
        //this.userManager?.signinRedirect();
      });
    //Clear "state" & code params from redirect url
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  public async handleLogin() {
    if (this.userManager == null) {
      this.fetchUserManager();
    }

    this.userManager?.signinRedirect();
  }
  /**
   * @deprecated The method should not be used
   */
  private async authorizeUser(authentikUser: AuthentikUser): Promise<void> {
    if (authentikUser.id_token) {
      localStorage.setItem('token', authentikUser.id_token);
      localStorage.setItem('access_token', authentikUser.access_token);

    }
    console.log('Authorized user')
    console.log('group: ', authentikUser.profile.groups)
    const stringRoles = authentikUser.profile.groups as string[];
    if (stringRoles && stringRoles.length > 0) {
      this.privateFields.roles = [];
      stringRoles.forEach((stringRole: any) => {
        this.roles?.push(Role[stringRole as keyof typeof Role]);
      });
    }

    if (this.roles?.includes(Role.Supplier)) {
      this.setUser(await UserService.getSupplierByEmail(authentikUser.profile.email!));
    } else if (this.roles?.includes(Role.Reseller)) {
      this.setUser(await UserService.getResellerByEmail(authentikUser.profile.email!));
    } else {
      this.setUser(await UserService.getUserByEmail(authentikUser.profile.email!));
    }

    if (this.user === null) {
      this.clearState();

      throw new RuntimeError('error.login.noRecordFound');
    }
  }
  /**
   * @deprecated The method should not be used
   */
  public getUserRole(): Role | null {
    if (this.roles && this.roles.length > 0) {
      if (this.roles.includes(Role.Supplier)) {
        return Role.Supplier;
      } else if (this.roles.includes(Role.Reseller)) {
        return Role.Reseller;
      } else {
        return Role.User; // Default role or fallback if none match
      }
    } else {
      console.log("no roles available");
    }
    return null; // No roles available
  }

  /**
   * Handles the log out of the user via a button.
   */
  async handleLogout() {
    // Invalid user manager, abort
    if (this.userManager == null) {
      this.fetchUserManager();
      return;
    }

    // Try to get saved user object.
    const authentikUser = await this.userManager.getUser();
    if (!authentikUser) {
      return;
    }

    this.clearState();
    this.userManager?.removeUser();
    this.userManager?.clearStaleState();
    this.setUser(null);
    // Redirect to authentiks logout url
    window.location.href = import.meta.env.VITE_LOGOUT_URL ?? '';
  }
}
